import { Form, FormGroup, Radio, RadioGroup, TextInput } from '@ksyos/design-system';
import { createRef, useEffect, useState } from 'react';
import type { IOrthCV1DiagnosticReportCustomControlData } from '../../../../../../common/validation/customControls/ophthalmology';
import type { ICustomControlDiagnosticReportAddEditProps } from '../../interfaces/';
import { BezoekAanSpecialistOption } from '../../../../../../common/enums/bezoekAanSpecialistOption';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { nullDiagnosis } from '../../../../../../common/validation/diagnosis';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { ButtonContainer, Feedback, HeaderDivider } from '../../../../elements/common';
import { FormContextProvider } from '../../../../elements/formContext';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { addFormAnalytics, addFormConversion, addTrackFormSubmit } from '../../utils/formAnalytics';
import { OrthCV1DiagnosticReportAddEditBeleid } from './OrthCV1DiagnosticReportAddEditBeleid';
import { OrthCV1DiagnosticReportAddEditBevindingen } from './OrthCV1DiagnosticReportAddEditBevindingen';
import { OrthCV1DiagnosticReportAddEditWerkdiagnose } from './OrthCV1DiagnosticReportAddEditWerkdiagnose';

export function OrthCV1DiagnosticReportAddEdit(props: ICustomControlDiagnosticReportAddEditProps) {
    const formRef = createRef<HTMLFormElement>();
    const saveSendButtonRef = createRef<HTMLButtonElement>();

    const [buttonDisabled, setButtonDisabled] = useState(false);

    useEffect(() => {
        const formNode = formRef.current;
        const saveSendButtonNode = saveSendButtonRef.current;
        addFormAnalytics(formNode, saveSendButtonNode);
    }, []);

    const date = props.process && props.process.dateSent;

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={date}
                isFirstItem={props.isFirstItem}
                isItemOpen
                organizationName={props.currentDiagnosticReport?.performerOrganization?.name}
                practitionerName={
                    props.currentDiagnosticReport && props.currentDiagnosticReport.performerPractitioner
                        ? getDisplayName(props.currentDiagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                title={translateKey('diagnosticReport.category_consultReport')}
            />

            <Form
                formRef={formRef}
                id="OrthCV1DiagnosticReportAddEditId"
                name="OrthCV1DiagnosticReportAddEdit"
                submitOnEnter={false}
            >
                <FormContextProvider
                    value={{
                        placeholderGroupName: 'diagnosticReport',
                        item: props.diagnosticReport.customControlData,
                        errors: props.errors,
                        onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                        onDoValidate: async (key) => await props.validateOneCustomControl(key),
                    }}
                >
                    <OrthCV1DiagnosticReportAddEditBevindingen
                        {...props}
                        resetWerkdiagnose={resetWerkdiagnose}
                    />

                    <OrthCV1DiagnosticReportAddEditWerkdiagnose
                        {...props}
                        resetWerkdiagnose={resetWerkdiagnose}
                    />
                    <HeaderDivider text="Advies" />

                    <OrthCV1DiagnosticReportAddEditBeleid {...props} />

                    <FormGroup
                        hasErrors={props.errors.has('customControlData.bezoek_aan_specialist_nu_nodig')}
                        label="Bezoek aan specialist nu nodig?"
                    >
                        <RadioGroup
                            className={`automation-bezoek_aan_specialist_nu_nodig ${
                                props.errors.has('customControlData.bezoek_aan_specialist_nu_nodig') ? 'has-danger' : ''
                            }`}
                            layout="row"
                            name="bezoek_aan_specialist_nu_nodig"
                            onChange={async (event) => {
                                await props.handleChangeCustomControl({
                                    bezoek_aan_specialist_nu_nodig: event.target.value,
                                });
                            }}
                            value={props.diagnosticReport.customControlData.bezoek_aan_specialist_nu_nodig}
                        >
                            <Radio
                                label="Ja"
                                value={BezoekAanSpecialistOption.ja}
                            />
                            <Radio
                                label="Nee"
                                value={BezoekAanSpecialistOption.nee}
                            />
                            <Radio
                                label="Zie beleid"
                                value={BezoekAanSpecialistOption.zieBeleid}
                            />
                        </RadioGroup>
                        <Feedback error={props.errors.get('customControlData.bezoek_aan_specialist_nu_nodig')} />
                    </FormGroup>

                    <FormGroup
                        hasErrors={props.errors.has('remark')}
                        label="Toelichting"
                    >
                        <TextInput
                            className={props.errors.has('remark') ? 'has-danger' : ''}
                            onChange={async (e) => {
                                await props.handleChange('remark', e.target.value ? e.target.value : null);
                                await props.validateOne('remark');
                            }}
                            placeholder={translateKey('common.remarksOrQuestions')}
                            value={props.diagnosticReport.remark ?? ''}
                        />
                        <Feedback error={props.errors.get('remark')} />
                    </FormGroup>
                </FormContextProvider>

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={props.onCancel}
                            entity={ProcessEntity.diagnosticReport}
                            isExisting={!!props.request.id}
                            serviceCode={props.service.code}
                            serviceType={props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={buttonDisabled}
                            save={async () => {
                                await saveDiagnosticReport('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={buttonDisabled}
                            saveSend={async () => {
                                await saveDiagnosticReport('final');
                            }}
                            saveSendButtonRef={saveSendButtonRef}
                        />,
                    ]}
                />
            </Form>
        </ProcessContainer>
    );

    async function resetWerkdiagnose() {
        await clearDiagnosis();
        await props.handleChangeCustomControl({ werkdiagnose: null });
    }

    async function clearDiagnosis() {
        await props.handleChange('diagnosis', { ...nullDiagnosis });
    }

    async function saveDiagnosticReport(saveType: 'draft' | 'final') {
        setButtonDisabled(true);
        addTrackFormSubmit(saveSendButtonRef.current);

        const validatedDiagnosticReport = await props.validateDiagnosticReport(saveType);
        if (!validatedDiagnosticReport) {
            setButtonDisabled(false);

            return;
        }

        const diagnosticReportId = await props.onSaveDiagnosticReport(saveType);

        if (saveType === 'draft') {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                props.service,
                'diagnosticReport',
                translateKey('toast.draftDefaultMessage')
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.submitSuccess',
                props.service,
                'diagnosticReport',
                translateKey('toast.answerSentToOrganizationSuccess', props.request.requesterOrganization?.name)
            );
            addFormConversion(formRef.current);
        }

        await props.onSave(diagnosticReportId);
    }
}

export function orthcV1DiagnosticReportInitializeCustomControlData(): IOrthCV1DiagnosticReportCustomControlData {
    return {
        bevindingen: {
            niet_te_beoordelen: false,
            niet_te_beoordelen_od: false,
            niet_te_beoordelen_os: false,
            geen_afwijkingen: false,
            geen_afwijkingen_od: false,
            geen_afwijkingen_os: false,
            andersText: null,
            andersText_od: false,
            andersText_os: false,
        },
        conclusie_diagnose: null,
        werkdiagnose: null,
        beleid: null,
        beleid_anders: null,
        urgentie_oogarts: null,
        urgentie_optometrist: null,
        urgentie_anders: null,
        termijn: null,
        bezoek_aan_specialist_nu_nodig: null,
    };
}
